import { useEffect, useMemo, useRef } from 'react';
import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { RefetchSupportedType } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useVersionHistoryContentVersionHistoryQuery as useVersionHistoryContentVersionHistoryQuery$data,
	useVersionHistoryContentVersionHistoryQueryVariables as useVersionHistoryContentVersionHistoryQuery$variables,
	useVersionHistoryContentVersionHistoryQuery_contentVersionHistory_ContentVersionHistoryConnection_nodes_ContentVersion as VersionHistoryNodeType,
} from './__types__/useVersionHistoryContentVersionHistoryQuery.ts';
import { filterExpiredVersions } from './versionHistoryUtils.ts';
import type { VersionHistoryContentType } from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';

type useVersionHistoryType = {
	contentType: VersionHistoryContentType;
	contentId: string;
	/** Also request who edited each version, including agents. Only the grouped list shows them. */
	includeCollaborators?: boolean;
	onExpiredVersionsFiltered?: (params: { filteredCount: number; totalCount: number }) => void;
};

export const useVersionHistory = ({
	contentType,
	contentId,
	includeCollaborators = false,
	onExpiredVersionsFiltered,
}: useVersionHistoryType): {
	autosavedVersions: readonly VersionHistoryNodeType[];
	loading: boolean;
	error: ApolloErrorInterop | undefined;
	loadMoreVersions: () => void;
	cursor: string | null;
	hasNextPage: boolean;
	refetch: RefetchSupportedType<
		useVersionHistoryContentVersionHistoryQuery$data,
		useVersionHistoryContentVersionHistoryQuery$variables
	>;
	filteredCount: number;
	totalCount: number;
} => {
	const { data, loading, error, fetchMore, refetch } = useQueryInterop<
		useVersionHistoryContentVersionHistoryQuery$data,
		useVersionHistoryContentVersionHistoryQuery$variables
	>(
		graphql`
			query useVersionHistoryContentVersionHistoryQuery(
				$contentId: ID!
				$filter: ContentVersionHistoryFilter!
				$after: String
				$includeCollaborators: Boolean = false
			) {
				contentVersionHistory(id: $contentId, after: $after, first: 30, filter: $filter) {
					nodes {
						author {
							id
							isActive
							displayName
						}
						collaborators @include(if: $includeCollaborators) {
							person {
								... on KnownUser {
									accountId
								}
								... on UnknownUser {
									accountId
								}
								displayName
								profilePicture {
									path
								}
							}
							agent {
								accountId
								displayName
								profilePicture {
									path
								}
							}
						}
						contentId
						number
						updatedTime
						contentProperties {
							latest {
								externalVersionId
								versionContainsAIContent
							}
						}
					}
					pageInfo {
						hasNextPage
						hasPreviousPage
						startCursor
						endCursor
					}
				}
			}
		`,
		{
			variables: { contentId, filter: { contentType }, includeCollaborators },
		},
	);
	const startCursor = data?.contentVersionHistory?.pageInfo?.startCursor || null;
	const rawHasNextPage = data?.contentVersionHistory?.pageInfo?.hasNextPage || false;

	const rawNodes = data?.contentVersionHistory?.nodes;

	const { autosavedVersions, hasFilteredOldVersions, filteredCount, totalCount } = useMemo(
		() => filterExpiredVersions(rawNodes),
		[rawNodes],
	);

	const hasNextPage = hasFilteredOldVersions ? false : rawHasNextPage;
	const cursor = hasFilteredOldVersions ? null : startCursor;

	const lastTrackedFilteredCountRef = useRef(0);
	useEffect(() => {
		if (!loading && filteredCount > 0 && filteredCount !== lastTrackedFilteredCountRef.current) {
			lastTrackedFilteredCountRef.current = filteredCount;
			onExpiredVersionsFiltered?.({ filteredCount, totalCount });
		}
	}, [filteredCount, totalCount, loading, onExpiredVersionsFiltered]);

	const loadMoreVersions = (): void => {
		if (hasNextPage && cursor && !loading) {
			void fetchMore({
				variables: { contentId, filter: { contentType }, includeCollaborators, after: cursor },
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult?.contentVersionHistory) {
						return prev;
					}

					const prevNodes = prev?.contentVersionHistory?.nodes || [];
					const fetchedNodes = fetchMoreResult.contentVersionHistory.nodes || [];
					const pageInfo =
						fetchMoreResult.contentVersionHistory.pageInfo || prev?.contentVersionHistory?.pageInfo;

					return {
						contentVersionHistory: {
							nodes: [...prevNodes, ...fetchedNodes],
							pageInfo,
							__typename: 'ContentVersionHistoryConnection',
						},
					};
				},
			});
		}
	};

	return {
		autosavedVersions,
		loading,
		error,
		loadMoreVersions,
		cursor,
		hasNextPage,
		refetch,
		filteredCount,
		totalCount,
	};
};
